<template>
    <div class="field-filter-picker">
        <!-- 輸入框 -->
        <div class="picker-input" @click="openDrawer">
            <Icon type="md-funnel" class="input-icon" />
            <span class="value-display">{{ filterSummary }}</span>
            <Icon type="ios-arrow-forward" class="arrow-icon" />
        </div>

        <!-- 選擇器 Drawer -->
        <Drawer
            v-model="showDrawer"
            title="案場過濾"
            :width="drawerWidth"
            placement="right"
            :closable="true"
            class-name="drawer-fullbody"
            :styles="{ zIndex: drawerZIndex }"
        >
            <div class="drawer-content">
                <!-- 當前選擇預覽 -->
                <div class="selection-preview">
                    <div class="preview-label">目前篩選</div>
                    <div class="preview-value">{{ filterSummary }}</div>
                </div>

                <!-- 左右選擇區域 -->
                <div class="picker-columns">
                    <!-- 左側：類型篩選 -->
                    <div class="picker-column">
                        <div class="column-title">
                            <Icon type="md-options" />
                            案場類型
                        </div>
                        <div class="column-options">
                            <div
                                v-for="type in filterTypes"
                                :key="type.key"
                                class="option-item"
                                :class="{ selected: selectedType === type.key }"
                                @click="selectType(type.key)"
                            >
                                <span class="option-label">{{ type.title }}</span>
                                <Icon v-if="selectedType === type.key" type="md-checkmark" class="check-icon" />
                            </div>
                        </div>
                    </div>

                    <!-- 右側：地區篩選 -->
                    <div class="picker-column">
                        <div class="column-title">
                            <Icon type="md-pin" />
                            地區
                        </div>
                        <div class="column-options">
                            <div
                                class="option-item"
                                :class="{ selected: selectedCity === '' }"
                                @click="selectCity('')"
                            >
                                <span class="option-label">全部地區</span>
                                <Icon v-if="selectedCity === ''" type="md-checkmark" class="check-icon" />
                            </div>
                            <div
                                v-for="city in availableCities"
                                :key="city.name"
                                class="option-item"
                                :class="{ selected: selectedCity === city.name, disabled: !city.available }"
                                @click="city.available && selectCity(city.name)"
                            >
                                <span class="option-label">{{ city.name }}</span>
                                <span class="option-count" v-if="city.count">({{ city.count }})</span>
                                <Icon v-if="selectedCity === city.name" type="md-checkmark" class="check-icon" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Drawer>
    </div>
</template>

<script>
import { fieldState } from '@shared/stores/fieldScope';
import { uiState } from '@shared/stores/uiState';
import { authService } from '@shared/services/auth/authService';

export default {
    name: 'FieldFilterDrawer',
    props: {
        modelType: {
            type: String,
            default: ''
        },
        modelCity: {
            type: String,
            default: ''
        },
        types: {
            type: Array,
            default: null
        }
    },
    emits: ['update:modelType', 'update:modelCity', 'change'],
    data() {
        return {
            showDrawer: false,
            selectedType: this.modelType,
            selectedCity: this.modelCity,
            drawerZIndex: 0
        };
    },
    computed: {
        // 使用 Pinia store（支援獨立模式）
        fieldScope() {
            return fieldState();
        },
        ui() {
            return uiState();
        },
        drawerWidth() {
            return this.ui?.drawerWidth || 320;
        },
        filterTypes() {
            if (this.types && this.types.length > 0) {
                return this.types;
            }
            const filterTypeList = this.fieldScope?.filterTypeList || [];
            return filterTypeList.filter(t => t.key !== 'hr');
        },
        // 根據選擇的類型動態計算可用城市
        availableCities() {
            // 使用 fieldScope.dataCity 作為基礎城市列表
            const baseCities = this.fieldScope?.dataCity || [];

            // 如果沒有選擇類型，直接使用 fieldScope 的 available 狀態
            if (!this.selectedType) {
                return baseCities.filter(c => typeof c === 'object' && c.name);
            }

            // 根據選擇的類型過濾案場，重新計算每個城市的可用性
            const allFields = this.fieldScope?.fields || [];
            const focusFields = this.fieldScope?.focusFields || [];

            let filteredFields;
            if (this.selectedType === 'FOCUS') {
                filteredFields = allFields.filter(f => f && focusFields.includes(f.id));
            } else {
                filteredFields = allFields.filter(f => {
                    if (!f) return false;
                    return f.type && f.type.includes(this.selectedType);
                });
            }

            // 計算在該類型下有案場的城市
            const availableCitySet = new Set(
                filteredFields
                    .map(f => this.fieldScope?.normalizeCity?.(f?.city) || f?.city)
                    .filter(city => city && city !== '' && city !== '---')
            );

            // 更新每個城市的 available 狀態
            return baseCities
                .filter(c => typeof c === 'object' && c.name)
                .map(c => ({
                    ...c,
                    available: availableCitySet.has(c.name)
                }));
        },
        filterSummary() {
            const parts = [];
            if (this.selectedType) {
                const type = this.filterTypes.find(t => t.key === this.selectedType);
                if (type) parts.push(type.title);
            } else {
                parts.push('全部類型');
            }
            if (this.selectedCity) {
                parts.push(this.selectedCity);
            }
            return parts.join(' · ') || '全部';
        }
    },
    watch: {
        modelType: {
            handler(val) {
                this.selectedType = val || '';
            },
            immediate: true
        },
        modelCity: {
            handler(val) {
                this.selectedCity = (val === '全部地區' || !val) ? '' : val;
            },
            immediate: true
        },
        showDrawer(newVal) {
            // 監聯 showDrawer 變化來管理 z-index
            if (!newVal && this.drawerZIndex > 0) {
                // 關閉時釋放 z-index
                this.ui?.releaseZIndex?.('filter', 'field');
                this.drawerZIndex = 0;
            }
        }
    },
    methods: {
        openDrawer() {
            this.selectedType = this.modelType || '';
            this.selectedCity = (this.modelCity === '全部地區' || !this.modelCity) ? '' : this.modelCity;
            // 獲取 z-index
            this.drawerZIndex = this.ui?.getNextZIndex?.('filter', 'field') || 1000;
            this.showDrawer = true;
        },
        closeDrawer() {
            this.showDrawer = false;
            // z-index 釋放由 watch 處理
        },
        selectType(key) {
            this.selectedType = key;

            // 檢查當前選擇的城市是否在新類型下仍然可用
            if (this.selectedCity) {
                const cityStillAvailable = this.availableCities.some(c => c.name === this.selectedCity);
                if (!cityStillAvailable) {
                    // 城市不可用，重置為全部地區
                    this.selectedCity = '';
                    this.$emit('update:modelCity', '');
                    localStorage.setItem('view_selectCity', '全部地區');
                }
            }

            // 同步到 fieldScope（轉換 key 為 title）
            if (this.fieldScope) {
                const filterType = this.filterTypes.find(t => t.key === key);
                const filterTitle = filterType?.title || '全部類型';
                this.fieldScope.selectFilter = filterTitle;
                this.fieldScope.selectFilterKey = key;

                // 保存到 localStorage
                localStorage.setItem('view_selectFilter', filterTitle);

                // 更新城市列表和案場過濾（無論 key 是否為空都要更新）
                if (this.fieldScope.updateCityList) {
                    this.fieldScope.updateCityList();
                }

                // 保存到 Supabase（異步，不阻塞 UI）
                this.savePreferencesToSupabase(filterTitle, this.fieldScope.selectCity);
            }

            this.$emit('update:modelType', key);
            this.$emit('change', { type: key, city: this.selectedCity });
        },
        selectCity(name) {
            this.selectedCity = name;
            const cityValue = name || '全部地區';

            // 同步到 fieldScope
            if (this.fieldScope) {
                this.fieldScope.selectCity = cityValue;

                // 保存到 localStorage
                localStorage.setItem('view_selectCity', cityValue);

                // 套用過濾器以更新 dataField
                if (this.fieldScope.applyInitialFilter) {
                    this.fieldScope.applyInitialFilter();
                }

                // 保存到 Supabase（異步，不阻塞 UI）
                this.savePreferencesToSupabase(this.fieldScope.selectFilter, cityValue);
            }

            this.$emit('update:modelCity', name);
            this.$emit('change', { type: this.selectedType, city: name });
        },
        /**
         * 保存過濾器偏好設定到 Supabase
         */
        async savePreferencesToSupabase(filter, city) {
            try {
                const user = authService.currentUser;
                const userId = user?.uid || user?.id;
                if (userId) {
                    await authService.saveFilterPreferences(userId, filter, city);
                }
            } catch (error) {
                // 靜默失敗，不影響用戶體驗
                console.warn('[FieldFilterDrawer] 保存偏好設定失敗:', error);
            }
        }
    }
};
</script>

<style lang="stylus" scoped>
.field-filter-picker
    display inline-block

.picker-input
    display flex
    align-items center
    gap 8px
    padding 6px 12px
    background #fff
    border 1px solid #dcdee2
    border-radius 4px
    cursor pointer
    transition all 0.2s

    &:hover
        border-color #57a3f3

.input-icon
    font-size 16px
    color #808695

.value-display
    flex 1
    font-size 14px
    color #515a6e

.arrow-icon
    font-size 14px
    color #c5c8ce

// Drawer 內容
.drawer-content
    display flex
    flex-direction column
    height 100%
    overflow hidden

.selection-preview
    padding 16px
    background linear-gradient(135deg, #667eea 0%, #764ba2 100%)
    color #fff
    flex-shrink 0

.preview-label
    font-size 12px
    opacity 0.8
    margin-bottom 4px

.preview-value
    font-size 18px
    font-weight 600

// 左右選擇區域
.picker-columns
    display flex
    flex 1
    min-height 0
    overflow hidden

.picker-column
    flex 1
    display flex
    flex-direction column
    border-right 1px solid #f0f0f0
    min-width 0

    &:last-child
        border-right none

.column-title
    display flex
    align-items center
    gap 8px
    padding 12px 16px
    font-size 13px
    font-weight 500
    color #808695
    background #f8f8f9
    flex-shrink 0

    .ivu-icon
        font-size 16px

.column-options
    flex 1
    overflow-y auto
    padding 8px

    &::-webkit-scrollbar
        width 4px

    &::-webkit-scrollbar-thumb
        background rgba(0, 0, 0, 0.15)
        border-radius 2px

.option-item
    display flex
    align-items center
    padding 10px 12px
    border-radius 6px
    cursor pointer
    transition all 0.15s

    &:hover
        background rgba(45, 140, 240, 0.08)

    &.selected
        background rgba(45, 140, 240, 0.12)

        .option-label
            color #2d8cf0
            font-weight 500

    &.disabled
        opacity 0.4
        cursor not-allowed

        &:hover
            background transparent

.option-label
    flex 1
    font-size 14px
    color #333

.option-count
    font-size 12px
    color #999
    margin-right 8px

.check-icon
    color #2d8cf0
    font-size 16px
    flex-shrink 0

// ===== Dark Theme =====
body.dark-theme
    .picker-input
        background #1a1d24
        border-color #3b4252

        &:hover
            border-color #5A9FFF

    .value-display
        color #e5e9f0

    .input-icon
        color #8892a0

    .picker-column
        border-right-color #3b4252

    .column-title
        background #252932
        color #8892a0

    .option-item
        &:hover
            background #3b4252

        &.selected
            background rgba(90, 159, 255, 0.15)

            .option-label
                color #5A9FFF

    .option-label
        color #e5e9f0

    .option-count
        color #8892a0

    .check-icon
        color #5A9FFF
</style>
